import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  createEquipmentThunk,
  deactivateEquipmentThunk,
  loadEquipment,
  setFilterInstrument,
  setFilterStatus,
  setPage,
  setSearch,
  updateEquipmentThunk,
} from '../features/maintenance/maintenanceSlice';
import type { Equipment, EquipmentPayload, EquipmentStatus, CalibrationStatus } from '../features/maintenance/types';
import { AppDispatch, RootState } from '../store/store';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { EquipmentForm } from '../components/EquipmentForm';
import { ExportPdfButton } from '../features/reports/ExportPdfButton';
import { ORG_WIDE_REPORT_RESOURCE_ID } from '../features/reports/types';
import { DateCell } from '../components/DateCell';

const STATUS_LABEL: Record<EquipmentStatus, string> = {
  operativo: 'Operativo',
  fuera_de_servicio: 'Fuera de servicio',
  en_reparacion: 'En reparación',
  baja: 'Baja',
};

const STATUS_CLASS: Record<EquipmentStatus, string> = {
  operativo: 'bg-green-100 text-green-800',
  fuera_de_servicio: 'bg-amber-100 text-amber-800',
  en_reparacion: 'bg-blue-100 text-blue-800',
  baja: 'bg-zinc-200 text-zinc-700',
};

const CALIBRATION_STATUS_LABEL: Record<CalibrationStatus, string> = {
  vigente: 'Vigente',
  por_vencer: 'Por vencer',
  vencido: 'Vencido',
  fuera_de_uso: 'Fuera de uso',
};

const CALIBRATION_STATUS_CLASS: Record<CalibrationStatus, string> = {
  vigente: 'bg-green-100 text-green-800',
  por_vencer: 'bg-amber-100 text-amber-800',
  vencido: 'bg-red-100 text-red-800',
  fuera_de_uso: 'bg-zinc-200 text-zinc-700',
};

export function EquipmentPage() {
  const dispatch = useDispatch<AppDispatch>();
  const { confirm } = useConfirmDialog();
  const { canEdit } = usePlatformAccessLevel('maintenance.access');
  const {
    equipment,
    equipmentLoading,
    pagination,
    search,
    filterStatus,
    filterInstrument,
  } = useSelector((s: RootState) => s.maintenance);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Equipment | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    dispatch(
      loadEquipment({
        search,
        status: filterStatus || undefined,
        isInstrument: filterInstrument ?? undefined,
        page: pagination.page,
        pageSize: pagination.pageSize,
      }),
    );
  }, [dispatch, search, filterStatus, filterInstrument, pagination.page, pagination.pageSize]);

  const openCreate = useCallback(() => {
    setEditing(null);
    setDialogOpen(true);
  }, []);

  const openEdit = useCallback((e: Equipment) => {
    setEditing(e);
    setDialogOpen(true);
  }, []);

  const handleSubmit = useCallback(
    async (payload: EquipmentPayload) => {
      setSubmitting(true);
      try {
        if (editing) {
          await dispatch(updateEquipmentThunk({ id: editing.id, payload })).unwrap();
        } else {
          await dispatch(createEquipmentThunk(payload)).unwrap();
        }
        setDialogOpen(false);
      } finally {
        setSubmitting(false);
      }
    },
    [dispatch, editing],
  );

  const handleDeactivate = useCallback(
    async (e: Equipment) => {
      const ok = await confirm({
        title: 'Dar de baja',
        message: `¿Dar de baja al equipo "${e.name}"?`,
        confirmText: 'Dar de baja',
        variant: 'danger',
      });
      if (!ok) return;
      await dispatch(deactivateEquipmentThunk(e.id));
    },
    [confirm, dispatch],
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <h1 className="text-2xl font-semibold">Equipos e instrumentos</h1>
        <div className="flex flex-wrap gap-2">
          <ExportPdfButton
            reportType="calibration_overdue"
            resourceId={ORG_WIDE_REPORT_RESOURCE_ID}
            permissionKey="maintenance.access"
            label="Informe calibración vencida"
            disabled={equipmentLoading}
          />
          {canEdit && (
            <button
              onClick={openCreate}
              className="rounded-md px-4 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary/90"
            >
              + Nuevo equipo
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <input
          type="text"
          placeholder="Buscar por código, nombre, marca, modelo, n° serie…"
          className="border rounded-md px-3 py-2 flex-1 min-w-[240px]"
          value={search}
          onChange={(e) => dispatch(setSearch(e.target.value))}
        />
        <select
          className="border rounded-md px-3 py-2"
          value={filterStatus}
          onChange={(e) => dispatch(setFilterStatus(e.target.value as EquipmentStatus | ''))}
        >
          <option value="">Todos los estados</option>
          {Object.entries(STATUS_LABEL).map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
        <select
          className="border rounded-md px-3 py-2"
          value={filterInstrument === null ? '' : String(filterInstrument)}
          onChange={(e) => {
            const v = e.target.value;
            dispatch(setFilterInstrument(v === '' ? null : v === 'true'));
          }}
        >
          <option value="">Todos</option>
          <option value="true">Solo instrumentos</option>
          <option value="false">Solo equipos</option>
        </select>
      </div>

      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <Th>Código</Th>
              <Th>Nombre</Th>
              <Th>Ubicación</Th>
              <Th>Criticidad</Th>
              <Th>Estado</Th>
              <Th>Calibración</Th>
              <Th>Próx. calibración</Th>
              <Th>Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {equipmentLoading && (
              <tr>
                <td colSpan={8} className="px-3 py-4 text-center text-muted-foreground">
                  Cargando…
                </td>
              </tr>
            )}
            {!equipmentLoading && equipment.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-4 text-center text-muted-foreground">
                  No hay equipos
                </td>
              </tr>
            )}
            {!equipmentLoading &&
              equipment.map((e) => (
                <tr key={e.id} className="border-t hover:bg-muted/40">
                  <td className="px-3 py-2 font-mono text-xs">{e.code}</td>
                  <td className="px-3 py-2">
                    <Link to={`/mantenimiento/equipos/${e.id}`} className="text-primary hover:underline">
                      {e.name}
                    </Link>
                    {e.isInstrument && (
                      <span className="ml-2 inline-block rounded bg-violet-100 text-violet-800 text-[10px] px-1.5 py-0.5">
                        instrumento
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">{e.location ?? '—'}</td>
                  <td className="px-3 py-2 capitalize">{e.criticality}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_CLASS[e.status]}`}>
                      {STATUS_LABEL[e.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {e.isInstrument && e.calibrationStatus ? (
                      <span
                        className={`inline-block rounded px-2 py-0.5 text-xs ${CALIBRATION_STATUS_CLASS[e.calibrationStatus]}`}
                      >
                        {CALIBRATION_STATUS_LABEL[e.calibrationStatus]}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {e.nextCalibrationAt ? <DateCell value={e.nextCalibrationAt} /> : '—'}
                  </td>
                  <td className="px-3 py-2">
                    {canEdit && (
                      <div className="flex gap-2">
                        <button
                          className="text-xs rounded bg-secondary px-2 py-1 hover:bg-secondary/80"
                          onClick={() => openEdit(e)}
                        >
                          Editar
                        </button>
                        {e.status !== 'baja' && (
                          <button
                            className="text-xs rounded bg-destructive text-destructive-foreground px-2 py-1 hover:bg-destructive/90"
                            onClick={() => handleDeactivate(e)}
                          >
                            Dar de baja
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Pager page={pagination.page} pages={pagination.pages} onChange={(p) => dispatch(setPage(p))} />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar equipo' : 'Nuevo equipo'}</DialogTitle>
          </DialogHeader>
          <EquipmentForm
            initial={editing}
            loading={submitting}
            onSubmit={handleSubmit}
            onCancel={() => setDialogOpen(false)}
            readOnly={!canEdit}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="text-left px-3 py-2 font-medium">{children}</th>;
}

function Pager({
  page,
  pages,
  onChange,
}: {
  page: number;
  pages: number;
  onChange: (p: number) => void;
}) {
  if (pages <= 1) return null;
  return (
    <div className="flex justify-end gap-2">
      <button
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        className="px-3 py-1 text-sm border rounded disabled:opacity-50"
      >
        Anterior
      </button>
      <span className="px-3 py-1 text-sm">
        Página {page} de {pages}
      </span>
      <button
        disabled={page >= pages}
        onClick={() => onChange(page + 1)}
        className="px-3 py-1 text-sm border rounded disabled:opacity-50"
      >
        Siguiente
      </button>
    </div>
  );
}
